import {
  AccessTime,
  Block,
  Cancel,
  CheckCircle,
  HighlightOff,
  SwapHoriz,
} from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";

import { EVOTRUX_BORDER_GREY } from "constants/index";

import type { ReactNode } from "react";

interface StatusPalette {
  color: string;
  backgroundColor: string;
  borderColor: string;
}

interface QuoteStatusDisplay extends StatusPalette {
  label: string;
  icon: ReactNode;
}

const SUCCESS_PALETTE: StatusPalette = {
  color: "#059669",
  backgroundColor: "#ecfdf5",
  borderColor: "#a7f3d0",
};

const WARNING_PALETTE: StatusPalette = {
  color: "#ea580c",
  backgroundColor: "#fff7ed",
  borderColor: "#fed7aa",
};

const ERROR_PALETTE: StatusPalette = {
  color: "#dc2626",
  backgroundColor: "#fef2f2",
  borderColor: "#fecaca",
};

const NEUTRAL_PALETTE: StatusPalette = {
  color: "#4b5563",
  backgroundColor: "#f9fafb",
  borderColor: EVOTRUX_BORDER_GREY,
};

const iconSx = { fontSize: 16 } as const;

interface GetQuoteStatusDisplayProps {
  quoteInternalState: InternalQuoteStatusValue;
  isLatestQuoteOwnedByRole: boolean;
}

/**
 * Maps a quote's internal state to how it should be presented in the status
 * bar. States that carry no status of their own (e.g. an active quote awaiting
 * a response) return null so no bar is rendered.
 */

function getQuoteStatusDisplay({
  quoteInternalState,
  isLatestQuoteOwnedByRole,
}: GetQuoteStatusDisplayProps): QuoteStatusDisplay | null {
  switch (quoteInternalState) {
    case "ACCEPTED":
      return {
        label: "Booked",
        icon: <CheckCircle sx={iconSx} />,
        ...SUCCESS_PALETTE,
      };
    case "COUNTERED":
      return {
        label: isLatestQuoteOwnedByRole
          ? "Countered by You"
          : "Countered by Carrier",
        icon: <SwapHoriz sx={iconSx} />,
        ...WARNING_PALETTE,
      };
    case "DECLINED":
      return {
        label: "Declined",
        icon: <Cancel sx={iconSx} />,
        ...ERROR_PALETTE,
      };
    case "CANCELLED":
      return {
        label: "Cancelled",
        icon: <Block sx={iconSx} />,
        ...NEUTRAL_PALETTE,
      };
    case "EXPIRED":
      return {
        label: "Expired",
        icon: <AccessTime sx={iconSx} />,
        ...NEUTRAL_PALETTE,
      };
    case "SHIPMENT_LOST":
      return {
        label: "Lost",
        icon: <HighlightOff sx={iconSx} />,
        ...NEUTRAL_PALETTE,
      };
    case "SHIPMENT_CANCELLED":
      return {
        label: "Shipment Cancelled",
        icon: <Block sx={iconSx} />,
        ...NEUTRAL_PALETTE,
      };
    case "SHIPMENT_EXPIRED":
      return {
        label: "Shipment Expired",
        icon: <AccessTime sx={iconSx} />,
        ...NEUTRAL_PALETTE,
      };
    default:
      return null;
  }
}

interface Props {
  quoteInternalState: InternalQuoteStatusValue;
  isLatestQuoteOwnedByRole: boolean;
}

export function QuoteStatusBar({
  quoteInternalState,
  isLatestQuoteOwnedByRole,
}: Props) {
  const statusDisplay = getQuoteStatusDisplay({
    quoteInternalState,
    isLatestQuoteOwnedByRole,
  });

  if (!statusDisplay) {
    return null;
  }

  const { label, icon, color, backgroundColor, borderColor } = statusDisplay;

  // Sized to its text rather than stretched to the cluster's width: a
  // full-width filled bar carries a control's affordance whatever colour it is,
  // which is the wrong signal directly above a set of real buttons.
  return (
    <Stack direction="row" width="100%" justifyContent="flex-start">
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        borderRadius={4}
        py={0.25}
        px={1}
        boxSizing="border-box"
        color={color}
        sx={{
          backgroundColor,
          border: `1px solid ${borderColor}`,
        }}
      >
        {icon}
        <Typography variant="body2">{label}</Typography>
      </Stack>
    </Stack>
  );
}
